import { GridColDef, GridColumnVisibilityModel, GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid';
import { Theme } from '@mui/material';
import React from 'react';
import DataGridComponent from '../../../common/dataGrid';
import TableUtils from '../../../../utils/tableUtils';
import LanguageUtils from '../../../../utils/LanguageUtils';
import { useIntl } from 'react-intl';
import TruncatedStringTooltip from '../../../common/truncatedStringTooltip';

interface IFactorSetsProps {
    classes?: any;
    theme?: Theme;
    checkSelection?: any;
    isLoading?: boolean;
    multipleSelection?: boolean;
    componentsProps?: any;
    onPageChange?: any;
    rows?: any;
    applyRowFn?: any;
    hiddenColumns?: Array<string>;
    onSortModelChange?: any;
    columnVisibilityModel?: GridColumnVisibilityModel;
    onColumnVisibilityModelChange?: any;
}

const messages = {
    name: LanguageUtils.createMessage('Factor set name'),
    departureType: LanguageUtils.createMessage('Departure type'),
    factor: LanguageUtils.createMessage('Factor'),
    surcharge: LanguageUtils.createMessage('Surcharge'),
    unitType: LanguageUtils.createMessage('Unit type'),
    all: LanguageUtils.createMessage('All'),
    surchargeCurrency: LanguageUtils.createMessage('Surcharge currency')
};

export default function ViewFactorSetDepartureLines(props: IFactorSetsProps): JSX.Element {
    const { componentsProps, isLoading, checkSelection, onPageChange, rows, hiddenColumns, multipleSelection, onSortModelChange,
        columnVisibilityModel, onColumnVisibilityModelChange } = props;

    const defaultFn = (cellValues: GridRenderCellParams | GridValueGetterParams) => cellValues.row;

    const applyRowFn = props.applyRowFn || defaultFn;

    const generateColumns = (): GridColDef[] => {
        const intl = useIntl();

        return [
            {
                field: 'name',
                headerName: intl.formatMessage(messages.name),
                flex: 1,
                sortable: true,
                renderCell: (params: GridRenderCellParams) => <TruncatedStringTooltip value={applyRowFn(params).name ?? '-'} />,
                hide: hiddenColumns && TableUtils.hideColumn('name', hiddenColumns)
            },
            {
                field: 'departureType',
                headerName: intl.formatMessage(messages.departureType),
                flex: 1,
                align: 'left',
                headerAlign: 'left',
                sortable: true,
                renderCell: (params: GridRenderCellParams) => {
                    return <><TruncatedStringTooltip value={`${applyRowFn(params).departureType?.name ?? ''} - ${applyRowFn(params).departureType?.code ?? ''}`} /></>;
                },
                hide: hiddenColumns && TableUtils.hideColumn('departureType', hiddenColumns)
            },
            {
                field: 'unitType',
                headerName: intl.formatMessage(messages.unitType),
                flex: 1,
                align: 'left',
                headerAlign: 'left',
                sortable: true,
                renderCell: (params: GridRenderCellParams) => {
                    if(applyRowFn(params).unitType) {
                        return <><TruncatedStringTooltip value={`${applyRowFn(params).unitType?.name ?? ''} - ${applyRowFn(params).unitType?.code ?? ''}`} /></>;
                    }

                    return <><TruncatedStringTooltip value={intl.formatMessage(messages.all)} /></>;
                },
                hide: hiddenColumns && TableUtils.hideColumn('unitType', hiddenColumns)
            },
            {
                field: 'factor',
                headerName: intl.formatMessage(messages.factor),
                flex: 1,
                align: 'left',
                headerAlign: 'left',
                sortable: true,
                renderCell: (params: GridRenderCellParams) => <TruncatedStringTooltip value={applyRowFn(params).factor ?? '-'} />,
                hide: hiddenColumns && TableUtils.hideColumn('factor', hiddenColumns)
            },
            {
                field: 'surcharge',
                headerName: intl.formatMessage(messages.surcharge),
                flex: 1,
                align: 'left',
                headerAlign: 'left',
                sortable: true,
                renderCell: (params: GridRenderCellParams) => <TruncatedStringTooltip value={applyRowFn(params).surcharge ?? '-'} />,
                hide: hiddenColumns && TableUtils.hideColumn('surcharge', hiddenColumns)
            },

            {
                field: 'surchargeCurrency',
                headerName: intl.formatMessage(messages.surchargeCurrency),
                flex: 1,
                align: 'left',
                headerAlign: 'left',
                sortable: true,
                renderCell: (params: GridRenderCellParams) => <TruncatedStringTooltip value={applyRowFn(params).surchargeCurrency?.name ?? '-'} />,
                hide: hiddenColumns && TableUtils.hideColumn('surchargeCurrency', hiddenColumns)
            }
        ];
    };

    return (
        <DataGridComponent rows={rows} multipleSelection={multipleSelection} columns={generateColumns()} componentsProps={componentsProps} isLoading={isLoading}
            checkSelection={checkSelection} onPageChange={onPageChange} onSortModelChange={onSortModelChange}
            columnVisibilityModel={columnVisibilityModel} onColumnVisibilityModelChange={onColumnVisibilityModelChange}/>
    );
}
